import { Alert, Card, Col, Divider, Form, FormInstance, InputNumber, Row, Typography } from "antd";
import useAccountingApi from "views/Settings/Accounting/hooks/useAccountingApi";
import useCoreStore from "views/hooks/useCoreStore";
import { useTranslation } from "react-i18next";
import { useWatch } from "antd/lib/form/Form";
import CardTitle from "components/CardTitle";
import { BiTransfer } from "react-icons/bi";
import { useEffect } from "react";
import React from "react";
const { Text } = Typography;

interface ExportFormModalPropsTypes {
  form: FormInstance;
}

export default function ExportForm({ form }: ExportFormModalPropsTypes): React.ReactElement {
  const { t } = useTranslation();
  const companySelected = useCoreStore(state => state.companySelected);
  const accountingFirmSelected = useCoreStore(state => state.accountingFirmSelected);
  const companySettings = useCoreStore(state => state.companySettings);
  const generalAccountsLength = useWatch("generalAccountsLength", form);
  const thirdPartiesAccountsLength = useWatch("thirdPartiesAccountsLength", form);
  const hasValidatedDocuments = companySettings?.hasValidatedDocuments;

  const { listPlatforms, platformsOptions } = useAccountingApi({
    form,
  });

  useEffect(() => {
    if (companySelected) {
      listPlatforms({
        variables: {
          filter: {
            accountingFirmId: accountingFirmSelected?.id,
          },
        },
      });
    }
  }, [companySelected]);

  const onValuesChange = (changedValues: any) => {
    if (changedValues.software) {
      const software = platformsOptions?.find(option => option?.value === changedValues?.software);
      form.setFieldsValue({
        prefixClientAccount: software?.defaultPrefixClientAccount,
      });
    }
  };

  return (
    <Card
      className="antd-newrai-card invoice-info-cards export-history-card"
      title={<CardTitle icon={<BiTransfer />} title={t("Transfer to accounts")} />}
    >
      {hasValidatedDocuments && (
        <Alert
          message={t(
            "You cannot change the export configuration because there are validated documents.",
          )}
          type="info"
        />
      )}
      <br />
      <Form
        name="updateExport"
        layout="vertical"
        form={form}
        onValuesChange={onValuesChange}
        autoComplete="off"
      >
        <Row gutter={8}>
          <Col span={24}>
            <Text style={{ fontSize: "16px" }}>{t("Length of counterparts")}</Text>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("General accounts length")}
              name="generalAccountsLength"
              colon={false}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <InputNumber disabled={hasValidatedDocuments} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("Third parties accounts length")}
              name="thirdPartiesAccountsLength"
              colon={false}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <InputNumber disabled={hasValidatedDocuments} />
            </Form.Item>
          </Col>
          <Divider style={{ margin: "8px 0" }} />
          <Col span={24}>
            <Row gutter={8}>
              <Col span={6}>
                <Text></Text>
              </Col>
              <Col span={9} style={{ marginBottom: "8px" }}>
                <Text>{t("Clients")}</Text>
              </Col>
              <Col span={9} style={{ marginBottom: "8px" }}>
                <Text>{t("Provider")}</Text>
              </Col>
            </Row>
            <Row gutter={8} style={{ marginBottom: "8px" }}>
              <Col span={6} style={{ display: "flex", alignItems: "center" }}>
                <Text style={{ fontSize: "14px" }}>{t("Account prefix")}</Text>
              </Col>
              <Col span={9}>
                <Form.Item name="prefixClientAccount" style={{ margin: 0 }}>
                  <InputNumber
                    maxLength={thirdPartiesAccountsLength}
                    disabled={hasValidatedDocuments}
                  />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item name="prefixProviderAccount" style={{ margin: 0 }}>
                  <InputNumber
                    maxLength={thirdPartiesAccountsLength}
                    disabled={hasValidatedDocuments}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8}>
              <Col span={6} style={{ display: "flex", alignItems: "center" }}>
                <Text style={{ fontSize: "14px" }}>{t("Default providers counterparts")}</Text>
              </Col>
              <Col span={9}>
                <Form.Item name="collectiveClientAccount" style={{ margin: 0 }}>
                  <InputNumber maxLength={generalAccountsLength} disabled={hasValidatedDocuments} />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item name="collectiveProviderAccount" style={{ margin: 0 }}>
                  <InputNumber maxLength={generalAccountsLength} disabled={hasValidatedDocuments} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Card>
  );
}
